/**
 * AttestationConsignationElecForm.jsx — Attestation de consignation électrique (NFC 18-510) Lafarge
 * Reproduit les blocs du template :
 *   - En-tête (date/durée validité, description, localisation, N° PP, entreprise)
 *   - Chargé de consignation Lafarge (nom, téléphone, habilitation)
 *   - Étape 1 — Séparation + Condamnation systèmes
 *   - Chargé de travaux EE (nom, niveau habilitation)
 *   - Étape 2 — VAT + MAT (court-circuit)
 *   - Délais : rendu fin travail, restitution urgence
 *   - Avis de fin de travail
 *   - Signataires
 */
import { Zap } from 'lucide-react';

function Checkbox({ checked, onChange, label, testid, className = '' }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer text-xs ${className}`} data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#DA291C] flex-shrink-0" />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

function TextInput({ value, onChange, testid, placeholder, className = '' }) {
  return (
    <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
      data-testid={testid} placeholder={placeholder}
      className={`px-2 py-1 text-xs border border-stone-300 focus:border-[#09090B] outline-none ${className}`} />
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>
      {children}
    </div>
  );
}

export default function AttestationConsignationElecForm({ extra, setExtra, dateDebut, dateFin, titre, lieu, entreprise, compact = false }) {
  return (
    <div className="space-y-4 border-2 border-yellow-500 bg-white" data-testid="attestation-consignation-elec-form">
      <div className="bg-yellow-500 text-stone-900 px-4 py-3 flex items-center gap-3">
        <Zap className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">ATTESTATION DE CONSIGNATION ÉLECTRIQUE</div>
          <div className="text-[10px] uppercase tracking-wider opacity-80">Consignation en deux étapes — Suivant NFC 18-510</div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        {/* En-tête — en mode compact, on garde uniquement N° PP (les autres champs sont hérités de l'AT) */}
        {compact ? (
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">N° PP (chantier sous-traité)</div>
              <TextInput value={extra.pp_numero} onChange={(v) => setExtra('pp_numero', v)} testid="ce-pp-numero" placeholder="Ex. PP-2026-0123" className="w-full" />
            </div>
          </div>
        ) : (
        <div>
          <SectionTitle>En-tête</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs">
            <div className="sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Description du travail à effectuer</div>
              <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{titre || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Localisation du travail</div>
              <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{lieu || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Entreprise intervenante</div>
              <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{entreprise || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Validité</div>
              <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateDebut || '—'} → {dateFin || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">N° PP (chantier sous-traité)</div>
              <TextInput value={extra.pp_numero} onChange={(v) => setExtra('pp_numero', v)} testid="ce-pp-numero" placeholder="Ex. PP-2026-0123" className="w-full" />
            </div>
          </div>
        </div>
        )}

        {/* Chargé de consignation */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Chargé de consignation Lafarge Ciments</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-3 gap-3 text-xs bg-red-50/30">
            <div className="sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Nom + Prénom</div>
              <TextInput value={extra.charge_consignation_nom} onChange={(v) => setExtra('charge_consignation_nom', v)}
                testid="ce-cdc-nom" placeholder="Nom du chargé de consignation Lafarge" className="w-full" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Téléphone</div>
              <TextInput value={extra.charge_consignation_tel} onChange={(v) => setExtra('charge_consignation_tel', v)}
                testid="ce-cdc-tel" placeholder="Portable" className="w-full" />
            </div>
            <div className="sm:col-span-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Niveau d'habilitation</div>
              <select value={extra.habilitation_cdc || ''} onChange={(e) => setExtra('habilitation_cdc', e.target.value)}
                data-testid="ce-cdc-habilitation" className="px-2 py-1 text-xs border border-stone-300">
                <option value="">—</option>
                <option value="BR">BR — Chargé d'intervention BT</option>
                <option value="BC">BC — Chargé de consignation BT</option>
                <option value="HC">HC — Chargé de consignation HT</option>
                <option value="B2V">B2V — Chargé de travaux BT</option>
                <option value="H2V">H2V — Chargé de travaux HT</option>
              </select>
            </div>
          </div>
        </div>

        {/* Étape 1 — Séparation + Condamnation */}
        <div>
          <SectionTitle>Étape 1 — Séparation & Condamnation</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs">
            <Checkbox checked={extra.etape1_separation} onChange={(v) => setExtra('etape1_separation', v)}
              label="Séparation de toutes les sources d'énergie effectuée (sectionnement visible)" testid="ce-et1-sep" />
            <Checkbox checked={extra.etape1_condamnation} onChange={(v) => setExtra('etape1_condamnation', v)}
              label="Condamnation en position d'ouverture des systèmes de manœuvre (cadenas + étiquette)" testid="ce-et1-cond" />
            <div className="text-[10px] italic text-stone-500">Réf : 5 étapes consignation NFC 18-510</div>
          </div>
        </div>

        {/* Chargé de travaux EE */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Chargé de travaux de l'entreprise intervenante</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-3 gap-3 text-xs bg-stone-50/50">
            <div className="sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Nom + Prénom</div>
              <TextInput value={extra.charge_travaux_ee_nom} onChange={(v) => setExtra('charge_travaux_ee_nom', v)}
                testid="ce-cdtee-nom" placeholder="Nom du chargé de travaux" className="w-full" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Niveau d'habilitation</div>
              <select value={extra.habilitation_cdtee || ''} onChange={(e) => setExtra('habilitation_cdtee', e.target.value)}
                data-testid="ce-cdtee-habilitation" className="px-2 py-1 text-xs border border-stone-300 w-full">
                <option value="">—</option>
                <option value="B1V">B1V — Exécutant BT</option>
                <option value="B2V">B2V — Chargé de travaux BT</option>
                <option value="H1V">H1V — Exécutant HT</option>
                <option value="H2V">H2V — Chargé de travaux HT</option>
              </select>
            </div>
          </div>
        </div>

        {/* Étape 2 — VAT + MAT */}
        <div>
          <SectionTitle accent="bg-orange-700">Étape 2 — Identification, VAT & Mise à la terre</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-orange-50/30">
            <Checkbox checked={extra.etape2_identification} onChange={(v) => setExtra('etape2_identification', v)}
              label="Identification de l'ouvrage / installation à consigner" testid="ce-et2-id" />
            <Checkbox checked={extra.etape2_vat} onChange={(v) => setExtra('etape2_vat', v)}
              label="Vérification d'absence de tension (VAT) au plus près de la zone de travail" testid="ce-et2-vat" />
            <Checkbox checked={extra.etape2_mat_cc} onChange={(v) => setExtra('etape2_mat_cc', v)}
              label="Mise à la terre et en court-circuit (obligatoire HT, recommandé BT)" testid="ce-et2-mat" />
          </div>
        </div>

        {/* Délais */}
        <div>
          <SectionTitle>Délais</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs bg-stone-50/50">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Avis de fin de travail rendu au plus tard</div>
              <input type="datetime-local" value={extra.fin_travail_limite || ''} onChange={(e) => setExtra('fin_travail_limite', e.target.value)}
                data-testid="ce-fin-limite" className="w-full px-2 py-1 text-xs border border-stone-300" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Délai de restitution en cas d'urgence</div>
              <div className="flex items-center gap-2">
                <TextInput value={extra.delai_urgence_h} onChange={(v) => setExtra('delai_urgence_h', v)} testid="ce-delai-h" placeholder="h" className="w-16" />
                <span className="text-[11px]">h</span>
                <TextInput value={extra.delai_urgence_min} onChange={(v) => setExtra('delai_urgence_min', v)} testid="ce-delai-min" placeholder="min" className="w-16" />
                <span className="text-[11px]">min</span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Attestation délivrée le</div>
              <input type="datetime-local" value={extra.delivrance_datetime || ''} onChange={(e) => setExtra('delivrance_datetime', e.target.value)}
                data-testid="ce-delivrance" className="px-2 py-1 text-xs border border-stone-300" />
            </div>
          </div>
        </div>

        {/* Avis de fin de travail */}
        <div>
          <SectionTitle accent="bg-green-700">Avis de fin de travail</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-green-50/30">
            <div className="text-[11px] italic text-stone-600 mb-1">
              Le chargé de travaux avise le chargé de consignation que les travaux désignés ci-dessus sont terminés.
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Date & heure de fin</div>
                <input type="datetime-local" value={extra.avis_fin_datetime || ''} onChange={(e) => setExtra('avis_fin_datetime', e.target.value)}
                  data-testid="ce-avis-fin" className="px-2 py-1 text-xs border border-stone-300 w-full" />
              </div>
              <div>
                <Checkbox checked={extra.travaux_termines} onChange={(v) => setExtra('travaux_termines', v)}
                  label="Travaux terminés — installation peut être remise sous tension" testid="ce-trav-term" />
                <Checkbox checked={extra.materiel_evacue} onChange={(v) => setExtra('materiel_evacue', v)}
                  label="Personnel et matériel évacués de la zone" testid="ce-mat-evac" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
